import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router';
import { Helmet } from 'react-helmet-async';

import { routerLocationPropType } from '../../proptypes';

/**
 * Meta container component for Helmet configuration.
 * Provides an interface to set multiple meta properties with desired specificity.
 */
const Meta = ({
  /**
   * Canonical URL of the page, ignored if not set.
   */
  canonical,
  /**
   * Page description for document, will cascade for social descriptions if others aren't set.
   */
  description,
  /**
   * Default description, if others aren't set.
   */
  defaultDescription,
  /**
   * Default social image, if others aren't set.
   */
  defaultImage,
  /**
   * Default title, if others aren't set.
   */
  defaultTitle,
  /**
   * Facebook application ID.
   */
  facebookAppId,
  /**
   * Description for Facebook meta.
   */
  facebookDescription,
  /**
   * Image for Facebook meta.
   */
  facebookImage,
  /**
   * Title for Facebook meta.
   */
  facebookTitle,
  /**
   * hreflang tags for page
   */
  hrefLang,
  /**
   * Location object from router.
   */
  location,
  /**
   * The next/prev pagination links
   */
  pagination,
  /**
   * String content for robots meta tag.
   */
  robots,
  /**
   * Schema.org meta objects.
   */
  schemata,
  /**
   * Description for social meta tags, can overriden by platform specific props.
   */
  socialDescription,
  /**
   * Image for social meta tags, can overriden by platform specific props.
   */
  socialImage,
  /**
   * Title for social meta tags, can overriden by platform specific props.
   */
  socialTitle,
  /**
   * Page title for document, will cascade for social titles if others aren't set.
   */
  title,
  /**
   * Title template for all titles.
   */
  titleTemplate,
  /**
   * Card type Twitter meta.
   */
  twitterCard,
  /**
   * Data property for Twitter meta.
   */
  twitterData1,
  /**
   * Data property for Twitter meta.
   */
  twitterData2,
  /**
   * Description for Twitter meta.
   */
  twitterDescription,
  /**
   * Image for Twitter meta.
   */
  twitterImage,
  /**
   * Label property for Twitter meta.
   */
  twitterLabel1,
  /**
   * Label property for Twitter meta.
   */
  twitterLabel2,
  /**
   * Username handle for Twitter meta.
   */
  twitterSite,
  /**
   * Title for Twitter meta.
   */
  twitterTitle,
  /**
   * Boolean whether to format the title using the template prop.
   */
  useTitleTemplate,
}) => {
  const formatTitle = (titleText) => {
    if (titleText) {
      if (useTitleTemplate) {
        return titleTemplate.replace(/%s/g, titleText);
      }
      return titleText;
    }
    return defaultTitle;
  };

  const OrganizationSchema = {
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'http://schema.org',
      '@type': 'Organization',
      name: 'Let’s Do This',
      image: `${MEDIA_ROOT}/icons/ldt/logo-square-icon-512x512.png`,
      url: 'https://www.letsdothis.com/',
      address: [
        {
          '@type': 'PostalAddress',
          streetAddress: '548 Market Street, PMB 40664',
          addressLocality: 'San Francisco',
          addressRegion: 'California',
          postalCode: '94104-5401',
          addressCountry: 'USA',
        },
        {
          '@type': 'PostalAddress',
          streetAddress: '90 High Holborn, 4th Floor',
          addressLocality: 'London',
          postalCode: 'WC1V 6LJ',
          addressCountry: 'GB',
        },
      ],
      sameAs: [
        'https://www.facebook.com/letsdothisuk',
        'https://twitter.com/letsdothisrace',
        'https://www.instagram.com/letsdothis.com_',
        'https://www.strava.com/clubs/252814/discussion',
        'https://www.linkedin.com/company/lets-do-this',
      ],
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.letsdothis.com/s?name={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    }),
  };

  return (
    <Helmet
      title={formatTitle(title)}
      meta={[
        { name: 'description', content: description || defaultDescription },
        ...(robots ? [{ name: 'robots', content: robots }] : []),
        /**
         * Facebook Open Graph, see http://ogp.me.
         */
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:title',
          content: formatTitle(facebookTitle || socialTitle || title),
        },
        {
          property: 'og:description',
          content:
            facebookDescription ||
            socialDescription ||
            description ||
            defaultDescription,
        },
        {
          property: 'og:image',
          content: facebookImage || socialImage || defaultImage,
        },
        {
          property: 'og:image:secure_url',
          content: facebookImage || socialImage || defaultImage,
        },
        { property: 'og:site_name', content: 'Let’s Do This' },
        {
          property: 'og:url',
          content: canonical || `${WEB_ROOT}${location.pathname}`,
        },
        /**
         * Facebook meta, see https://developers.facebook.com/docs/sharing/webmasters.
         */
        { property: 'fb:app_id', content: facebookAppId },
        /**
         * Twitter Card, see https://dev.twitter.com/cards/types/summary.
         */
        { name: 'twitter:site', content: twitterSite },
        { name: 'twitter:card', content: twitterCard },
        {
          name: 'twitter:title',
          content: formatTitle(twitterTitle || socialTitle || title),
        },
        {
          name: 'twitter:description',
          content:
            twitterDescription ||
            socialDescription ||
            description ||
            defaultDescription,
        },
        {
          name: 'twitter:image',
          content: twitterImage || socialImage || defaultImage,
        },
        /**
         * Deprecated Twitter Product Card values, still consumed by Slack https://dev.twitter.com/cards/types/product.
         */
        ...(twitterLabel1
          ? [{ name: 'twitter:label1', content: twitterLabel1 }]
          : []),
        ...(twitterData1
          ? [{ name: 'twitter:data1', content: twitterData1 }]
          : []),
        ...(twitterLabel2
          ? [{ name: 'twitter:label2', content: twitterLabel2 }]
          : []),
        ...(twitterData2
          ? [{ name: 'twitter:data2', content: twitterData2 }]
          : []),
      ]}
      link={[
        ...(canonical ? [{ rel: 'canonical', href: canonical }] : []),
        ...(hrefLang
          ? hrefLang.map((lang) => ({
              href: lang.href,
              hrefLang: lang.hreflang,
              rel: lang.rel,
            }))
          : []),
        ...pagination,
      ]}
      script={[...(schemata || []), OrganizationSchema]}
    />
  );
};

Meta.propTypes = {
  canonical: PropTypes.string,
  description: PropTypes.string,
  defaultDescription: PropTypes.string,
  defaultImage: PropTypes.string,
  defaultTitle: PropTypes.string,
  facebookAppId: PropTypes.string,
  facebookDescription: PropTypes.string,
  facebookImage: PropTypes.string,
  facebookTitle: PropTypes.string,
  hrefLang: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      hreflang: PropTypes.string.isRequired,
      rel: PropTypes.string.isRequired,
    }),
  ),
  location: routerLocationPropType.isRequired,
  pagination: PropTypes.arrayOf(
    PropTypes.shape({
      rel: PropTypes.string,
      href: PropTypes.string,
    }),
  ),
  robots: PropTypes.string,
  schemata: PropTypes.arrayOf(
    PropTypes.shape({
      innerHTML: PropTypes.string,
      type: PropTypes.string,
    }),
  ),
  socialDescription: PropTypes.string,
  socialImage: PropTypes.string,
  socialTitle: PropTypes.string,
  title: PropTypes.string,
  titleTemplate: PropTypes.string,
  twitterCard: PropTypes.string,
  twitterData1: PropTypes.string,
  twitterData2: PropTypes.string,
  twitterDescription: PropTypes.string,
  twitterImage: PropTypes.string,
  twitterLabel1: PropTypes.string,
  twitterLabel2: PropTypes.string,
  twitterSite: PropTypes.string,
  twitterTitle: PropTypes.string,
  useTitleTemplate: PropTypes.bool,
};

Meta.defaultProps = {
  canonical: undefined,
  description: undefined,
  defaultDescription:
    'Discover epic endurance events with Let’s Do This. Find and book' +
    ' the best running, triathlon, cycling and obstacle events from around the world.',
  defaultImage: `${MEDIA_ROOT}/images/logo-social.jpg`,
  defaultTitle: 'Let’s Do This — What will you do next?',
  facebookAppId: FACEBOOK_APP_ID,
  facebookDescription: undefined,
  facebookImage: undefined,
  facebookTitle: undefined,
  hrefLang: [],
  pagination: [],
  robots: undefined,
  schemata: undefined,
  socialDescription: undefined,
  socialImage: undefined,
  socialTitle: undefined,
  title: undefined,
  titleTemplate: '%s — Let’s Do This',
  twitterCard: 'summary_large_image',
  twitterData1: undefined,
  twitterData2: undefined,
  twitterDescription: undefined,
  twitterImage: undefined,
  twitterLabel1: undefined,
  twitterLabel2: undefined,
  twitterSite: '@letsdothisrace',
  twitterTitle: undefined,
  useTitleTemplate: true,
};

export default withRouter(Meta);
